import { useCallback, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { useUiParameters } from '@queries';
import { Box, Button, Divider, Heading3, Heading4, Modal, u } from '@ui';

import { ParameterFilter, ParameterFilterProvider } from '../ParameterFilter';

import { ParameterGroupList } from './components/PositionGroup';
import * as css from './ParameterSelectionModal.css';
import { useSyncParameterUpdates } from './useSyncParameterUpdates';

import type { ParameterFilterFormValues } from '../ParameterFilter/ParameterFilterProvider';

type ParameterSelectionModalProps = {
    layoutId: number;
    nodeId: string;
    isOpen: boolean;
    onDismiss: () => void;
    activeSwingId: string | undefined;
    activeUserId: string | undefined;
    parameterIds: string[];
};

export function ParameterSelectionModal({
    isOpen,
    layoutId,
    nodeId,
    parameterIds,
    onDismiss,
    ...rest
}: ParameterSelectionModalProps) {
    return (
        <Modal
            title="Parameter Selection"
            open={isOpen}
            onDismiss={onDismiss}
            variant="full"
            footer={{ primary: <Button onClick={onDismiss}>Close</Button> }}
        >
            <ParameterFilterProvider initialParameterIds={parameterIds}>
                <ParameterSelectionModalContent layoutId={layoutId} nodeId={nodeId} {...rest} />
            </ParameterFilterProvider>
        </Modal>
    );
}

type ParameterSelectionModalContentProps = Pick<ParameterSelectionModalProps, 'layoutId' | 'nodeId'> &
    Pick<ParameterSelectionModalProps, 'activeSwingId' | 'activeUserId'>;

const ParameterSelectionModalContent = ({
    layoutId,
    nodeId,
    activeSwingId,
    activeUserId,
}: ParameterSelectionModalContentProps) => {
    const { scheduleSync } = useSyncParameterUpdates({ layoutId, nodeId });

    return (
        <Box width="100%" position="relative" minWidth={0} isolate alignSelf="stretch">
            <Box paddingBlockEnd={u(2)} position="sticky" top={0} bg="raised" px={u(4)} mx={-32} z={4}>
                <SelectedParametersPreview scheduleSync={scheduleSync} />
                <ParameterFilter />
            </Box>

            <ParameterGroupList activeSwingId={activeSwingId} activeUserId={activeUserId} scheduleSync={scheduleSync} />
        </Box>
    );
};

const toggleParameterId = (selectedParameterIds: string[], id: string) =>
    selectedParameterIds.includes(id)
        ? selectedParameterIds.filter((parameterId) => parameterId !== id)
        : [...selectedParameterIds, id];

type SelectedParametersPreviewProps = {
    scheduleSync: (selectedParameterIds: string[]) => void;
};

export const SelectedParametersPreview = ({ scheduleSync }: SelectedParametersPreviewProps) => {
    const { control, setValue } = useFormContext<ParameterFilterFormValues>();
    const selectedParameterIds = useWatch({ control, name: 'selectedParameterIds' });
    const toggleParameter = useCallback(
        (id: string) => {
            const nextSelectedParameterIds = toggleParameterId(selectedParameterIds, id);
            setValue('selectedParameterIds', nextSelectedParameterIds, { shouldDirty: true });
            scheduleSync(nextSelectedParameterIds);
        },
        [scheduleSync, selectedParameterIds, setValue],
    );
    const clearSelectedParameters = useCallback(() => {
        const nextSelectedParameterIds: string[] = [];
        setValue('selectedParameterIds', nextSelectedParameterIds, { shouldDirty: true });
        scheduleSync(nextSelectedParameterIds);
    }, [scheduleSync, setValue]);
    const { data: parameters = [], isLoading: areParametersLoading } = useUiParameters();
    const selectedParameters = useMemo(() => {
        const idSet = new Set(selectedParameterIds);
        return parameters.filter((p) => idSet.has(p.id));
    }, [parameters, selectedParameterIds]);

    const t = useTranslate();

    if (!selectedParameterIds?.length || areParametersLoading) return null;

    return (
        <Box zIndex={1}>
            <Box display="flex" paddingBlockEnd={u(2)} gap={u(2)} alignItems="center">
                <Heading3>Selected Parameters</Heading3>

                <Heading4 className={css.counter} p={u(1)}>
                    {selectedParameterIds.length}
                </Heading4>

                <Button size="tiny" variant="secondary" icon="close" onClick={clearSelectedParameters}>
                    Clear All
                </Button>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={u(1)} paddingBlockEnd={u(1)} className={css.buttonRows}>
                {selectedParameters.map((parameter) => (
                    <Button
                        key={parameter.id}
                        size="tiny"
                        variant="secondary"
                        icon="close"
                        onClick={() => toggleParameter(parameter.id)}
                    >
                        {t(parameter.i18n ?? '-')}
                    </Button>
                ))}
            </Box>
            <Divider />
        </Box>
    );
};
